import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";

import { listTenants, type TenantOut } from "../api/admin";
import { useAuth } from "../lib/auth";

/**
 * The tenant a platform admin with no home tenant should land in: the desk tenant (the
 * strategist's own workspace) when there is one, else the first active tenant.
 */
export function defaultTenant(tenants: TenantOut[]): TenantOut | null {
  const active = tenants.filter((t) => t.active);
  return active.find((t) => t.kind === "desk") ?? active[0] ?? null;
}

/** Platform admins only: choose which tenant the UI acts in (sent as X-Tenant-Id). */
export function TenantSwitcher() {
  const { user, selectedTenantId, selectTenant } = useAuth();
  const tenants = useQuery({
    queryKey: ["admin", "tenants"],
    queryFn: listTenants,
    enabled: Boolean(user?.is_platform_admin),
  });
  // A platform admin created from the CLI has no home tenant, and every tenant-scoped route
  // answers 400 until X-Tenant-Id is sent. Pick one rather than leave each page in an error
  // state behind a select that only looks like it has a value.
  const needsTenant = Boolean(user?.is_platform_admin) && user?.tenant_id == null && selectedTenantId === null;
  useEffect(() => {
    if (!needsTenant || !tenants.data) return;
    const pick = defaultTenant(tenants.data);
    if (pick) selectTenant(pick.id);
  }, [needsTenant, tenants.data, selectTenant]);

  if (!user?.is_platform_admin) return null;
  const value = selectedTenantId ?? user.home_tenant_id ?? "";
  return (
    <label className="flex items-center gap-2 text-sm">
      <span className="text-zinc-500 dark:text-zinc-400">Tenant</span>
      <select
        aria-label="Active tenant"
        className="max-w-[14rem] rounded-md border border-zinc-300 bg-white px-2 py-1 text-sm dark:border-zinc-700 dark:bg-zinc-900"
        value={value}
        onChange={(e) => {
          const id = Number(e.target.value);
          if (!Number.isFinite(id) || id === 0) return;
          selectTenant(id === user.home_tenant_id ? null : id);
        }}
      >
        {value === "" ? (
          <option value="" disabled>
            Select a tenant…
          </option>
        ) : null}
        {(tenants.data ?? []).map((t) => (
          <option key={t.id} value={t.id}>
            {t.name}
            {t.kind === "desk" ? " (desk)" : ""}
            {t.active ? "" : " (inactive)"}
          </option>
        ))}
      </select>
    </label>
  );
}
